import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import { ArcOptions } from '@cmps/ng-chart';

import { CmpsSdpNumberPipe } from '../common/pipe/single-decimal-point';

import {
    ConfigViewMetric,
    ConfigViewPillar,
    Metric,
} from './config.insight.model';
import { OptionalColumn } from './optional-column.insight.model';
import {
    MetricData,
    MetricDataItem,
    percentToTargetRanked,
} from './performance-data/metric.pd.model';
import {
    VisualItemClickEvent,
    VisualItemLayout,
} from './visual-item.insight.model';

interface RowData {
    dataMetric: MetricDataItem;
    chartOpts: ArcOptions;
    chartData: any[];
}

interface RowItem extends RowData {
    id: string;
    viewMetric: ConfigViewMetric;
    metric: Metric;
    peerGroup: RowData;
}

@Component({
    selector: '[cmpsInsightVisualItem]',
    templateUrl: './visual-item.insight.component.html',
})
export class VisualItemInsightComponent implements OnChanges, OnDestroy {
    @Input() pillar: ConfigViewPillar;
    @Input() metrics: Metric[];
    @Input() data: MetricData;
    @Input() peerGroupData: MetricData;
    @Input() vil: VisualItemLayout;
    @Input() showThresholdColours: boolean;
    @Input() showTrendArrows: boolean;
    @Input() optionalColumns: OptionalColumn[];
    @Output() itemClick: EventEmitter<VisualItemClickEvent>;

    rows: RowItem[][];

    constructor(private readonly _sdpNumberPipe: CmpsSdpNumberPipe) {
        this.itemClick = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('pillar') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('metrics') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('data') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('peerGroupData') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('vil')
        ) {
            this._layoutRows();
        }
    }

    ngOnDestroy(): void {
        this.itemClick.complete();
    }

    onChartClick($event: MouseEvent | KeyboardEvent, rowItem: RowItem): void {
        $event.stopPropagation();
        this.itemClick.emit({
            viewPillar: this.pillar,
            viewMetric: rowItem.viewMetric,
            metric: rowItem.metric,
            dataMetric: rowItem.dataMetric,
        });
    }

    _layoutRows(): void {
        if (
            this.vil.layout.itemsPerRow == null ||
            this.vil.layout.itemsPerRow <= 0
        ) {
            return;
        }
        this.rows = Array.from({
            length: Math.ceil(
                this.pillar.metrics.length / this.vil.layout.itemsPerRow,
            ),
        });
        let rowIndex = 0;
        let rowPairIndex = 0;
        let colIndex = 0;
        let rowColIndexStart = 0;
        while (colIndex < this.pillar.metrics.length) {
            const viewMetric = this.pillar.metrics[colIndex];
            if (!this.rows[rowIndex]) {
                this.rows[rowIndex] = [];
            }
            this.rows[rowIndex].push({
                id: `chart-${viewMetric.metricId}-${Math.floor(
                    Math.random() * 1000,
                )}`,
                viewMetric,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                metric:
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this.metrics &&
                    this.metrics.find(
                        (e) => e.metricId === viewMetric.metricId,
                    ),
                ...this._formatData(viewMetric),
                peerGroup:
                    this.peerGroupData && this._formatData(viewMetric, true),
            });
            colIndex++;
            if (
                colIndex % this.vil.layout.itemsPerRow === 0 ||
                colIndex === this.pillar.metrics.length
            ) {
                rowPairIndex++;
                if (rowPairIndex < 2) {
                    colIndex = rowColIndexStart;
                } else {
                    rowPairIndex = 0;
                    rowColIndexStart = colIndex;
                }
                rowIndex++;
            }
        }
    }

    private _formatData(
        viewMetric: ConfigViewMetric,
        peerGroup = false,
    ): RowData {
        const data = !peerGroup ? this.data : this.peerGroupData;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const dataMetric =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            data && data.metrics.find((m) => m.id === viewMetric.metricId);
        const pttR = !dataMetric
            ? null
            : percentToTargetRanked(
                  dataMetric.actual,
                  dataMetric.target,
                  dataMetric.rankOrder,
              );
        const percentToTarget =
            !dataMetric || pttR == null ? 0 : Math.abs(pttR);

        let height = 100;
        let label = {
            formatter: () =>
                !dataMetric
                    ? null
                    : this._sdpNumberPipe.transform(dataMetric.actual),
            maxFontSize: 23,
        };
        let innerRadius = 0.82;
        let color = !dataMetric ? null : dataMetric.targetThresholdColor;
        if (peerGroup) {
            height *= 1.35;
            label = undefined;
            innerRadius = 0.92;
            color = !dataMetric ? null : '#0064a7';
        }

        const chartOpts = {
            rect: { width: '100%', height, top: 5, bottom: 5 },
            label,
            innerRadius,
        };
        const chartData = [
            {
                x: '', // TODO revise
                y: percentToTarget,
                color,
            },
            {
                x: 'gap', // TODO revise
                y: Math.min(Math.max(1 - percentToTarget, 0), 1),
                color: 'grey',
            },
        ];

        return { dataMetric, chartOpts, chartData };
    }
}
